import { Copy06 } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { copyToClipboard, getShortName, onError } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import TooltipCmp from '../TooltipCmp';
import TruncateTextCmp from '../TruncateText';

interface ICarrierDetailItem {
    label: string;
    value: any;
    // When set, this node is rendered as the row value instead of `value` (used for
    // styled values like the carrier-specific tracking-id name prefix). The plain
    // `value` is still used as the truncate tooltip message text.
    valueNode?: React.ReactNode;
    // When set, a copy icon is shown after the value that copies this raw text to the
    // clipboard (e.g. the tracking id for common carriers, the tracking url for private).
    copyValue?: string;
}

interface ICarrierDetailTooltipProps {
    imageUrl?: string | null;
    name?: string;
    // Optional secondary line under the carrier name (e.g. a contact name, or "MC #1235"),
    // mirroring the Carrier & Pricing Details card on the order detail page. Omitted by
    // callers that have nothing to show there, which keeps the single-line layout unchanged.
    contactLine?: string | null;
    // Rendered in place of the initials circle when there is no imageUrl — used by callers
    // presenting the org as the carrier (visibility off / unassigned) to show the WAL logo
    // box instead of initials. Omitted → initials fallback, unchanged for existing callers.
    imageFallbackNode?: React.ReactNode;
    details?: ICarrierDetailItem[];
}

const CarrierDetailRow = ({ label, value, valueNode, copyValue }: ICarrierDetailItem) => {
    const { t } = useTranslation();
    const valueRef = useRef<HTMLParagraphElement>(null);

    return (
        <li className="flex items-center justify-between gap-x-2">
            <p className="text-xs leading-[1.5] font-normal text-textSecondary flex-none">{label}</p>
            <div className="flex items-center gap-x-1.5 overflow-hidden min-w-0">
                <TruncateTextCmp message={value} refName={valueRef}>
                    <p ref={valueRef} className="text-xs leading-[1.5] font-medium text-grayLight900 text-right truncate">
                        {valueNode ?? value}
                    </p>
                </TruncateTextCmp>
                {copyValue && (
                    <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={t('Common.Copy')}>
                        <Copy06
                            onClick={(event) => {
                                event.stopPropagation();
                                copyToClipboard(copyValue);
                                WalToast.copy(t('Common.Copied_to_clipboard') as string, '', 'copy-msg');
                            }}
                            className="text-gray500 w-3.5 h-3.5 cursor-pointer flex-none"
                        />
                    </TooltipCmp>
                )}
            </div>
        </li>
    );
};

const CarrierDetailTooltip = ({ imageUrl, name, contactLine, imageFallbackNode, details = [] }: ICarrierDetailTooltipProps) => {
    const { t } = useTranslation();
    const carrierNameRef = useRef<HTMLHeadingElement>(null);
    const carrierContactLineRef = useRef<HTMLParagraphElement>(null);

    return (
        <div className="space-y-3">
            <p className="text-xs font-semibold text-grayLight900">{t('Common.Carrier_Details')}</p>
            <div className="flex gap-x-2">
                {imageUrl ? (
                    <img className="table-profile-icn-circle-xs rounded-md flex-none" src={imageUrl} alt={name || ''} onError={onError} />
                ) : (
                    imageFallbackNode ?? <div className="table-profile-customer-circle-xs rounded-md flex-none">{getShortName(name || '-')}</div>
                )}
                <div className="overflow-hidden space-y-0.5 flex-1">
                    <TruncateTextCmp message={name} refName={carrierNameRef}>
                        <h6 ref={carrierNameRef} className="text-xs leading-[1.5] font-medium text-grayLight900 truncate">
                            {name || '-'}
                        </h6>
                    </TruncateTextCmp>
                    {contactLine && (
                        <TruncateTextCmp message={contactLine} refName={carrierContactLineRef}>
                            <p ref={carrierContactLineRef} className="text-xs leading-[1.5] font-normal text-grayLight600 truncate">
                                {contactLine}
                            </p>
                        </TruncateTextCmp>
                    )}
                </div>
            </div>
            {details.length > 0 && (
                <ul className="space-y-1.5 border-t border-utilityGray100 mt-2.5 pt-2.5">
                    {details.map((item, index) => (
                        <CarrierDetailRow key={index} label={item?.label} value={item?.value} valueNode={item?.valueNode} copyValue={item?.copyValue} />
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CarrierDetailTooltip;
